import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { SegmentedControl } from '@/components/ui/segmented-control';
import ImageStage from '@/components/ui/image-stage';
import type { ImageAssetType } from '@/components/ui/carousel/carousel.types';
import { cn } from '@/lib/utils';
import { roomTypeLabel, styleLabel } from './stageable';

type BeforeAfterPreviewProps = {
  original: ImageAssetType;
  stagedUrl: string;
  roomType: string;
  style: string;
};

const originalUrl = (image: ImageAssetType) =>
  image.caches?.['1920w'] ||
  image.caches?.fullSize ||
  image.caches?.fullsize ||
  image.caches?.['830w'];

/**
 * Shared preview sizing for the staging modal (viewer-size, 90vh max):
 * height-first like asset-view-modal.tsx — the stage takes the viewport minus
 * the modal chrome (header + thumb strip + selects + hint + footer, ~380px) so
 * the form and footer never fall below the fold, and its width follows a 3:2
 * landscape ratio, capped at the modal's own 95vw ceiling minus its `p-6`
 * gutters. The 240px floor keeps short viewports usable (the modal body still
 * scrolls there).
 *
 * The size is stated in px/vh rather than left to the image, because the
 * `viewer` modal is `w-auto`: an image-sized preview means an empty box at
 * open, a jump when the image decodes, and another on every before/after or
 * batch switch. Reserving it up front is what ImageStage exists for — see
 * `ui/image-stage.tsx` for the letterboxing tradeoff that buys.
 */
const PREVIEW_HEIGHT = 'max(240px, calc(90vh - 380px))';

export const PREVIEW_STAGE_CLASS = 'mx-auto rounded-lg bg-muted';
export const PREVIEW_STAGE_STYLE = {
  height: PREVIEW_HEIGHT,
  width: `min(calc(95vw - 3rem), calc(${PREVIEW_HEIGHT} * 3 / 2))`
} as const;

/** Result-phase compare view: the real staged CDN derivative vs the original. */
const BeforeAfterPreview = ({
  original,
  stagedUrl,
  roomType,
  style
}: BeforeAfterPreviewProps) => {
  const [view, setView] = useState<'before' | 'after'>('after');
  const isAfter = view === 'after';
  return (
    <ImageStage
      src={isAfter ? stagedUrl : originalUrl(original)}
      alt={
        isAfter
          ? `Virtually staged: ${original.caption ?? 'photo'}`
          : original.caption ?? 'Original photo'
      }
      // The original's thumbnail is cached either way, so the after view
      // blurs up from the room it was staged from rather than from nothing.
      placeholderSrc={original.caches?.['300w']}
      className={PREVIEW_STAGE_CLASS}
      style={PREVIEW_STAGE_STYLE}
    >
      <span
        className={cn(
          'absolute bottom-3 left-3 flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold text-white',
          isAfter ? 'bg-primary' : 'bg-black/70'
        )}
      >
        {isAfter ? (
          <>
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            After · {styleLabel(style)} {roomTypeLabel(roomType)}
          </>
        ) : (
          <>Before · Original Room</>
        )}
      </span>
      <SegmentedControl
        className="absolute right-3 top-3 rounded-md bg-background/90"
        name="staging-compare"
        aria-label="Compare staging"
        value={view}
        onValueChange={(value) => setView(value as 'before' | 'after')}
        options={[
          { label: 'Before', value: 'before' },
          { label: 'After', value: 'after' }
        ]}
      />
    </ImageStage>
  );
};

export default BeforeAfterPreview;
